import React from 'react';
import { useHistory } from 'react-router';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Button, CrossCircleIcon, HelpIcon, TickCircleIcon } from '@motorway/mw-highway-code';

import { GA_TAGS, ULEZ_CHECKER_EVENTS } from 'Utilities/analytics';
import { unprettyVRM } from 'Utilities/formValidators';

import Tooltip from '../../../../../../../client/assets_seller/ulez/chart/tooltip.svg';
import { formatVehicleMakePlural } from '../../../../../../utilities/vehicles/utils';
import { CUSTOMER_HUB_VALUATION_ROUTE } from '../../../../customerHub/CustomerHubHelpers';
import { ULEZ_STATUS } from '../../UlezPage.helpers';
import LocalTexts from '../../ulezTextV2.json';

import { Disclaimer } from './SubComponents/Disclaimer/Disclaimer';
import { Heading } from './SubComponents/Heading/Heading';

import styles from './ComplianceV2.scss';

const LocalT = new MDText(LocalTexts);

export type ComplianceV2Props = {
  isCompliant: boolean | null;
  isEuroStatus3: boolean;
  make?: string;
  vrm?: string;
};

type ComplianceResources = {
  icon: React.ReactElement;
  key: string;
}

const ComplianceV2 = ({ isCompliant, isEuroStatus3, make, vrm }: ComplianceV2Props) => {
  const history = useHistory();

  const cvtRedirectCTALabel = LocalT.translate('results.complianceCta') as string;

  const getCompliance = () => {
    let complianceObj: ComplianceResources;
    const euro3ComplianceObj: ComplianceResources = {
      icon: <HelpIcon />,
      key: ULEZ_STATUS.EURO_3.text,
    };

    switch (isCompliant) {
    case null:
      complianceObj = { icon: <HelpIcon filled={true} />, key: ULEZ_STATUS.UNAVAILABLE.text };
      break;
    case false:
      complianceObj = { icon: <CrossCircleIcon filled={true} />, key: ULEZ_STATUS.NON_COMPLIANT.text };
      break;
    default:
      complianceObj = { icon: <TickCircleIcon filled={true} />, key: ULEZ_STATUS.COMPLIANT.text };
      break;
    }
    return isEuroStatus3 ? euro3ComplianceObj : complianceObj;
  };

  const onCtaClick = (isUlezCompliant: boolean | null) => {
    let actionLabel: string;
    if (isUlezCompliant === null) {
      actionLabel = ULEZ_STATUS.UNAVAILABLE.actionLabel;
    } else if (isEuroStatus3) {
      actionLabel = ULEZ_STATUS.EURO_3.actionLabel;
    } else if (isUlezCompliant) {
      actionLabel = ULEZ_STATUS.COMPLIANT.actionLabel;
    } else {
      actionLabel = ULEZ_STATUS.NON_COMPLIANT.actionLabel;
    }

    ULEZ_CHECKER_EVENTS.CTA_CLICKED({
      buttonLabel: cvtRedirectCTALabel,
      categoryType: isUlezCompliant ? 'compliant' : 'non_compliant',
      label: vrm ? unprettyVRM(vrm) : 'N/A',
      url: CUSTOMER_HUB_VALUATION_ROUTE,
    });
    GA_TAGS.ULEZ_VALUATION_SUBMITTED(actionLabel, 'Main');
    history.push(CUSTOMER_HUB_VALUATION_ROUTE);
  };

  const complianceInfo = getCompliance();
  const hasComplianceStatus = isCompliant !== null && !isEuroStatus3;

  const isEuroOrNonAvailableStatus = isEuroStatus3 || isCompliant === null;
  const isNonCompliantStatus = hasComplianceStatus && !isCompliant;

  return (
    <div className={styles.componentWrapper}>
      <div className={cx(styles.component, {
        [styles.euroStatus3]: isEuroOrNonAvailableStatus,
      })}
      >
        <Heading
          complianceInfo={complianceInfo}
          hasComplianceStatus={hasComplianceStatus}
          isCompliant={isCompliant}
          isEuroStatus3={isEuroStatus3}
        />
        <div className={cx(styles.cardBody, {
          [styles.nonCompliant]: isNonCompliantStatus,
          [styles.euroStatus3]: isEuroOrNonAvailableStatus,
        })}>
          <div className={styles.left}>
            <div className={styles.body}>
              <p className={styles.bodyPrimaryText} data-testid='ulezPrimaryText'>
                {
                  (isEuroOrNonAvailableStatus)
                    ? LocalT.translate('results.notAvailable.desc')
                    : LocalT.translate(`results.${complianceInfo.key}.desc`)
                }
              </p>

              <p className={styles.bodySecondaryText}>
                {LocalT.translate('results.trackYourVehicle', { make: formatVehicleMakePlural(make) || 'car\'s' })}
              </p>
            </div>
            <div className={cx(styles.cta, {
              [styles.euroStatus3]: isEuroOrNonAvailableStatus,
            })}>
              { <Button icon="chevron" label={cvtRedirectCTALabel} onClick={() => onCtaClick(isCompliant)} /> }
              { <Disclaimer /> }
            </div>
          </div>
          <div className={cx(styles.right, {
            [styles.nonCompliant]: isNonCompliantStatus,
            [styles.euroStatus3]: isEuroOrNonAvailableStatus,
          })}>
            <img
              alt={LocalT.translate('results.tooltipAlt') as string}
              className={cx(styles.tooltip, {
                [styles.nonCompliant]: isNonCompliantStatus,
                [styles.euroStatus3]: isEuroOrNonAvailableStatus,
              })}
              height="89"
              loading="lazy"
              src={Tooltip}
              width="64"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceV2;
